import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { TabSpec, TitledTabs } from "components/common/titledTabs";
import { MultiSelectAutocomplete } from "components/common/MultiSelectAutocomplete";
import { useTenantTranslation } from "hooks/formatters";
import { useScrapEyeTenantSelection } from "hooks/useScrapEyeTenantSelection";
import { useMemo, useState } from "react";
import { CrossTenantLabellerOverviewTab } from "./CrossTenantLabellerOverviewTab";
import { CrossTenantReviewerOverviewTab } from "./CrossTenantReviewerOverviewTab";
import { CrossTenantDailyActivityTab } from "./CrossTenantDailyActivityTab";

type TabKey = "labellers" | "reviewers" | "dailyActivity";

export const ManageLabellersOverviewPage = () => {
  const { t } = useTenantTranslation();
  const [selectedTab, setSelectedTab] = useState<TabKey>("labellers");
  const {
    tenantOptions,
    activeTenantIds,
    onTenantSelectionChange,
    filterTenantOption,
    isLoading,
  } = useScrapEyeTenantSelection();

  const tabSpecs: TabSpec<TabKey>[] = useMemo(
    () => [
      {
        title: t("labellers"),
        key: "labellers" as const,
        content: (
          <CrossTenantLabellerOverviewTab tenantNames={activeTenantIds} />
        ),
      },
      {
        title: t("reviewers"),
        key: "reviewers" as const,
        content: (
          <CrossTenantReviewerOverviewTab tenantNames={activeTenantIds} />
        ),
      },
      {
        title: t("dailyActivity"),
        key: "dailyActivity" as const,
        content: <CrossTenantDailyActivityTab tenantNames={activeTenantIds} />,
      },
    ],
    [t, activeTenantIds]
  );

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={3} sx={{ p: 3, height: "100%" }}>
      <Typography variant="h4">{t("labellerStats")}</Typography>
      <MultiSelectAutocomplete
        options={tenantOptions}
        selectedIds={activeTenantIds}
        onChange={onTenantSelectionChange}
        getOptionLabel={(option) => option.longName}
        filterOption={filterTenantOption}
        label={t("tenants")}
        chipPlacement="below"
      />
      <TitledTabs
        tabSpecs={tabSpecs}
        selectedTabKey={selectedTab}
        onChange={setSelectedTab}
      />
    </Stack>
  );
};
